/**
 * OSM building fill-extrusion + MapLibre light for Digital Twin floor sense.
 */

import {
  BUILDING_EXTRUSION_HEIGHT_EXPR,
  MAP_LIGHT_DAY,
  MAP_LIGHT_NIGHT,
} from "./map-style.js";

export const BUILDING_3D_LAYER_ID = "sentinel-building-3d";
const BUILDING_SOURCE = "openmaptiles";
const BUILDING_SOURCE_LAYER = "building";

/**
 * @param {import('maplibre-gl').Map} map
 * @param {"day"|"night"} theme
 */
export function applyMapViewportLight(map, theme) {
  if (!map || typeof map.setLight !== "function") return;
  try {
    map.setLight(theme === "night" ? MAP_LIGHT_NIGHT : MAP_LIGHT_DAY);
  } catch (_) {}
}

/**
 * Remove every fill-extrusion layer (OpenFreeMap styles ship null-unsafe paints).
 * @param {import('maplibre-gl').Map} map
 */
export function stripAllFillExtrusionLayers(map) {
  if (!map || typeof map.getStyle !== "function") return;
  try {
    const style = map.getStyle();
    const layers = (style && style.layers) || [];
    for (const layer of layers) {
      if (!layer || layer.type !== "fill-extrusion") continue;
      try {
        if (map.getLayer(layer.id)) map.removeLayer(layer.id);
      } catch (_) {}
    }
  } catch (_) {}
}

/**
 * Insert 3D buildings below first symbol layer when style uses OpenMapTiles.
 * Uses a constant-safe height expr (no raw null → to-number).
 * @param {import('maplibre-gl').Map} map
 */
export function upsertOsmBuildingExtrusion(map) {
  if (!map || !map.isStyleLoaded()) return;
  try {
    stripAllFillExtrusionLayers(map);
    if (map.getLayer(BUILDING_3D_LAYER_ID)) return;
    const sources = map.getStyle()?.sources || {};
    if (!sources[BUILDING_SOURCE]) return;
    const layers = map.getStyle()?.layers || [];
    let beforeId = "";
    for (const layer of layers) {
      if (layer.type === "symbol") {
        beforeId = layer.id;
        break;
      }
    }
    map.addLayer(
      {
        id: BUILDING_3D_LAYER_ID,
        source: BUILDING_SOURCE,
        "source-layer": BUILDING_SOURCE_LAYER,
        type: "fill-extrusion",
        minzoom: 13,
        paint: {
          "fill-extrusion-color": "#e2e8f0",
          "fill-extrusion-height": BUILDING_EXTRUSION_HEIGHT_EXPR,
          "fill-extrusion-base": 0,
          "fill-extrusion-opacity": 0.88,
        },
      },
      beforeId || undefined,
    );
  } catch (_) {}
}

/**
 * Drop fill-extrusion layers when MapLibre workers throw null-number evaluate errors.
 * @param {import('maplibre-gl').Map} map
 */
export function armBuildingExtrusionErrorGuard(map) {
  if (!map || map.__crocBuildingErrGuard) return;
  map.__crocBuildingErrGuard = true;
  map.on("error", (ev) => {
    const msg = String((ev && ev.error && ev.error.message) || (ev && ev.message) || "");
    if (!/Expected value to be of type number/i.test(msg) && !/found null instead/i.test(msg)) {
      return;
    }
    stripAllFillExtrusionLayers(map);
  });
}

/**
 * @param {import('maplibre-gl').Map} map
 * @param {"day"|"night"} theme
 */
export function applyDigitalTwinMapEnhancements(map, theme) {
  armBuildingExtrusionErrorGuard(map);
  applyMapViewportLight(map, theme);
  upsertOsmBuildingExtrusion(map);
}
